import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { Color } from '@adornis/chemistry/elements/theming/coloring-system.js';
import type { Course } from '@chancenwerk/shared/courses/Course.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import type { CourseDetailCardAction, UserFilter } from './board-component-course-detail-card.js';

/**
 * @customElement board-component-course-aggregated-detail-card
 */
@customElement('board-component-course-aggregated-detail-card')
export class BoardComponentCourseAggregatedDetailCard extends ChemistryLitElement {
  @property({ attribute: false }) public coursesToManage = new RXController(this, [] as Course[]);
  @property({ attribute: false }) public kpf = new RXController(this, new KPF({}));
  @property({ attribute: true, reflect: true }) public expandable = true;
  @property({ attribute: false }) public deleteDigital = false;
  @property({ attribute: 'hide-delete', type: Boolean }) public hideDelete = false;
  @property({ attribute: 'hide-edit', type: Boolean }) public hideEdit = false;
  @property({ attribute: false }) public dropzonesEnabled = ['SchuKo', 'LeKo', 'OS', 'US'];
  @property({ attribute: true }) public showAggregatedView = true;
  @property({ attribute: false }) public userFilter: Maybe<UserFilter>;
  @property({ attribute: false }) public actions: Maybe<CourseDetailCardAction[]> = null;
  /** Courses of the same KPF handled by other cards; forwarded so a drop can clear them. */
  @property({ attribute: false }) public otherCourses: Course[] = [];

  override render() {
    if (this.coursesToManage.value.length === 0) {
      return nothing;
    }

    return html`
      <cr-card ${css({ background: this._getCardBackgroundColor() })}>
        <cr-flex horizontal space="sm">
          ${this.expandable
            ? html`
                <cr-card
                  center
                  crossaxis-center
                  h100
                  ${css({
                    background: this.colors.primary.shade(300),
                    height: 'auto',
                    cursor: 'pointer',
                    padding: this.spacing.sm,
                    borderRadius: this.sizes.borderRadiusSecondary,
                  })}
                  @click=${() => (this.showAggregatedView = !this.showAggregatedView)}
                >
                  <cr-icon ${css({ color: 'white', fontSize: '2rem' })}>
                    ${!this.showAggregatedView ? 'compress' : 'expand'}
                  </cr-icon>
                </cr-card>
              `
            : nothing}

          <cr-flex flex space="sm">
            ${this.showAggregatedView ? this._getAggregatedView() : this._getNonAggregatedView()}
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private _getAggregatedView() {
    return html`<board-component-course-detail-card
      .dropzonesEnabled=${this.dropzonesEnabled}
      .kpf=${this.kpf.value}
      .coursesToManage=${this.coursesToManage}
      .otherCourses=${this.otherCourses}
      .deleteDigital=${this.deleteDigital}
      .userFilter=${this.userFilter}
      .actions=${this.actions}
      @deleted=${(e: CustomEvent) => {
        const event = new CustomEvent('deleted', { detail: { value: e.detail.value } });
        this.dispatchEvent(event);
        this.requestUpdate();
      }}
      @dropped=${async (e: CustomEvent) => {
        const event = new CustomEvent('dropped', { detail: { value: e.detail.user } });
        this.dispatchEvent(event);
        this.requestUpdate();
      }}
      @dropped-elsewhere=${() => {
        const event = new CustomEvent('dropped-elsewhere', {});
        this.dispatchEvent(event);
        this.requestUpdate();
      }}
      @course-edited=${() => {
        this.dispatchEvent(new CustomEvent('course-edited'));
        this.requestUpdate();
      }}
      ?hide-delete=${this.hideDelete}
      ?hide-edit=${this.hideEdit}
    ></board-component-course-detail-card>`;
  }

  private _getCardBackgroundColor() {
    const blueColor = Color.make('blue').shade(200);
    const course = this.coursesToManage.value.at(0);

    if (course?.startDate) {
      if (course.startDate.startOf('day') <= DateTime.now().startOf('day')) {
        return this.colors.primary.shade(100);
      } else if (course.startDate.startOf('day') > DateTime.now().startOf('day')) {
        return blueColor;
      }
    }
    if (course?.plannedStartDate) {
      if (course.plannedStartDate.startOf('day') > DateTime.now().startOf('day')) return blueColor;
    }

    return this.colors.tone.error.shade(200);
  }

  private _getNonAggregatedView() {
    return html` ${repeat(
      this.coursesToManage.value,
      c => c._id,
      c => html`
        <board-component-course-detail-card
          .dropzonesEnabled=${this.dropzonesEnabled}
          .kpf=${this.kpf}
          .coursesToManage=${[c]}
          .otherCourses=${this.otherCourses}
          .deleteDigital=${this.deleteDigital}
          @deleted=${(e: CustomEvent) => {
            const event = new CustomEvent('deleted', { detail: { value: e.detail.value } });
            this.dispatchEvent(event);
            this.requestUpdate();
          }}
          @dropped=${async (e: CustomEvent) => {
            const event = new CustomEvent('dropped', { detail: { value: e.detail.user } });
            this.dispatchEvent(event);
            this.requestUpdate();
          }}
          @dropped-elsewhere=${() => {
            const event = new CustomEvent('dropped-elsewhere', {});
            this.dispatchEvent(event);
            this.requestUpdate();
          }}
          @course-edited=${() => {
            this.dispatchEvent(new CustomEvent('course-edited'));
            this.requestUpdate();
          }}
          ?hide-delete=${this.hideDelete}
          ?hide-edit=${this.hideEdit}
        ></board-component-course-detail-card>
      `,
    )}`;
  }
}
